import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import {
  createDroneMappingUrl,
  deleteDroneMapping,
  formatDroneMappingSize,
  listDroneMappings,
  publishDroneMapping,
  validateDroneMappingFile,
} from '../services/droneMappingService';
import { fitMapBoundsSafely } from '../utils/leafletMapSafety';
import './MapeamentoDrone.css';

function formatarDataVoo(dataVoo) {
  if (!dataVoo) return '';
  const [ano, mes, dia] = String(dataVoo).slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : dataVoo;
}

// Renderiza o COG direto do storage: o navegador baixa apenas os blocos
// visíveis via HTTP Range, então nenhum arquivo inteiro é carregado.
export function MapeamentoDroneLayer({ url, opacity = 0.85, onStatus }) {
  const map = useMap();
  const layerRef = useRef(null);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  useEffect(() => {
    if (!map || !url) return undefined;
    let cancelado = false;

    (async () => {
      try {
        statusRef.current?.({ carregando: true, erro: '' });
        const [{ default: parseGeoraster }, { default: GeoRasterLayer }] = await Promise.all([
          import('georaster'),
          import('georaster-layer-for-leaflet'),
        ]);
        const georaster = await parseGeoraster(url);
        if (cancelado) return;

        const camada = new GeoRasterLayer({
          georaster,
          opacity,
          resolution: 256,
          zIndex: 540,
        });
        camada.addTo(map);
        layerRef.current = camada;

        const bounds = camada.getBounds?.();
        if (bounds?.isValid?.()) {
          fitMapBoundsSafely(map, [
            [bounds.getSouth(), bounds.getWest()],
            [bounds.getNorth(), bounds.getEast()],
          ], { maxZoom: 18, padding: [24, 24] });
        }
        statusRef.current?.({ carregando: false, erro: '' });
      } catch (err) {
        console.error('Erro ao abrir mapeamento de drone:', err);
        if (!cancelado) {
          statusRef.current?.({
            carregando: false,
            erro: 'Não foi possível abrir este mapeamento. Confirme se o arquivo é um GeoTIFF otimizado para nuvem (COG).',
          });
        }
      }
    })();

    return () => {
      cancelado = true;
      if (layerRef.current) {
        try { map.removeLayer(layerRef.current); } catch (_erro) { /* mapa pode já ter sido desmontado */ }
        layerRef.current = null;
      }
    };
    // Opacidade é ajustada no efeito abaixo sem recriar a camada.
  }, [map, url]);

  useEffect(() => {
    layerRef.current?.setOpacity?.(opacity);
  }, [opacity]);

  return null;
}

export function MapeamentoDronePanel({
  propriedade,
  user,
  onFechar,
  camadaAtiva,
  onAtivar,
  opacidade,
  onOpacidade,
  statusCamada,
}) {
  const [mapeamentos, setMapeamentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [publicando, setPublicando] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);
  const [abrindoId, setAbrindoId] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [nomeVoo, setNomeVoo] = useState('');
  const [dataVoo, setDataVoo] = useState(() => new Date().toISOString().slice(0, 10));

  const propriedadeId = propriedade?.id;
  const podeEditar = propriedade?._accessRole !== 'viewer';

  const carregarLista = useCallback(async () => {
    if (!propriedadeId) return;
    setCarregando(true);
    setErro('');
    const resultado = await listDroneMappings(propriedadeId);
    if (!resultado.success) setErro('Não foi possível carregar o histórico de mapeamentos.');
    setMapeamentos(resultado.data);
    setCarregando(false);
  }, [propriedadeId]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  const selecionarArquivo = event => {
    const file = event.target.files?.[0] || null;
    setArquivo(file);
    setErro('');
    if (file && !nomeVoo) setNomeVoo(file.name.replace(/\.[^.]+$/, ''));
  };

  const publicar = async event => {
    event.preventDefault();
    if (!arquivo || publicando) return;
    try {
      validateDroneMappingFile(arquivo);
    } catch (err) {
      setErro(err.message);
      return;
    }
    setPublicando(true);
    setErro('');
    setMensagem('Enviando mapeamento… arquivos grandes podem demorar alguns minutos.');
    const resultado = await publishDroneMapping({
      propriedadeId,
      userId: user?.id,
      file: arquivo,
      nome: nomeVoo,
      dataVoo,
    });
    setPublicando(false);
    if (resultado.success) {
      setMensagem('Mapeamento publicado no histórico.');
      setFormAberto(false);
      setArquivo(null);
      setNomeVoo('');
      await carregarLista();
    } else {
      setMensagem('');
      setErro(`Falha ao publicar: ${resultado.error}`);
    }
  };

  const abrirNoMapa = async mapeamento => {
    if (camadaAtiva?.id === mapeamento.id) {
      onAtivar(null);
      return;
    }
    setAbrindoId(mapeamento.id);
    setErro('');
    const resultado = await createDroneMappingUrl(mapeamento.arquivo_path);
    setAbrindoId(null);
    if (resultado.success) {
      onAtivar({ id: mapeamento.id, nome: mapeamento.nome, url: resultado.url });
    } else {
      setErro('Não foi possível abrir este mapeamento agora.');
    }
  };

  const excluir = async mapeamento => {
    const confirmado = globalThis.confirm?.(`Excluir o mapeamento "${mapeamento.nome}" definitivamente?`);
    if (!confirmado) return;
    setExcluindoId(mapeamento.id);
    const resultado = await deleteDroneMapping(mapeamento);
    setExcluindoId(null);
    if (resultado.success) {
      if (camadaAtiva?.id === mapeamento.id) onAtivar(null);
      setMensagem('Mapeamento excluído.');
      await carregarLista();
    } else {
      setErro(`Falha ao excluir: ${resultado.error}`);
    }
  };

  const podeExcluir = mapeamento => (
    propriedade?._accessRole === 'owner' || mapeamento.user_id === user?.id
  );

  return (
    <section className="drone-map-panel" aria-label="Mapeamentos de drone da propriedade">
      <header>
        <div>
          <span>Mapeamento de drone</span>
          <strong>{propriedade?.nome}</strong>
        </div>
        <button type="button" onClick={onFechar} aria-label="Fechar mapeamentos de drone">×</button>
      </header>

      {camadaAtiva && (
        <div className="drone-map-panel__active">
          <div>
            <span>Exibindo no mapa</span>
            <strong>{camadaAtiva.nome}</strong>
            {statusCamada?.carregando && <small>Carregando camada…</small>}
          </div>
          <label>
            Transparência
            <input
              type="range"
              min="20"
              max="100"
              value={Math.round(opacidade * 100)}
              onChange={event => onOpacidade(Number(event.target.value) / 100)}
              aria-label="Transparência do mapeamento"
            />
          </label>
          <button type="button" onClick={() => onAtivar(null)}>Ocultar camada</button>
        </div>
      )}

      {statusCamada?.erro && <p className="drone-map-panel__error" role="alert">{statusCamada.erro}</p>}
      {erro && <p className="drone-map-panel__error" role="alert">{erro}</p>}
      {mensagem && !erro && <p className="drone-map-panel__notice" role="status">{mensagem}</p>}

      <div className="drone-map-panel__list" aria-label="Histórico de voos">
        {carregando && <p className="drone-map-panel__hint">Carregando histórico…</p>}
        {!carregando && mapeamentos.length === 0 && (
          <p className="drone-map-panel__hint">
            Nenhum mapeamento publicado para esta propriedade.
            {podeEditar ? ' Publique o ortomosaico do voo em GeoTIFF otimizado (COG).' : ''}
          </p>
        )}
        {mapeamentos.map(mapeamento => (
          <article key={mapeamento.id} className={camadaAtiva?.id === mapeamento.id ? 'is-active' : ''}>
            <div>
              <strong>{mapeamento.nome}</strong>
              <small>
                Voo em {formatarDataVoo(mapeamento.data_voo)} · {formatDroneMappingSize(mapeamento.tamanho_bytes)}
              </small>
            </div>
            <div className="drone-map-panel__item-actions">
              <button
                type="button"
                onClick={() => abrirNoMapa(mapeamento)}
                disabled={abrindoId === mapeamento.id}
              >
                {abrindoId === mapeamento.id ? 'Abrindo…' : camadaAtiva?.id === mapeamento.id ? 'Ocultar' : 'Ver no mapa'}
              </button>
              {podeExcluir(mapeamento) && (
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => excluir(mapeamento)}
                  disabled={excluindoId === mapeamento.id}
                >
                  {excluindoId === mapeamento.id ? 'Excluindo…' : 'Excluir'}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      {podeEditar && !formAberto && (
        <button type="button" className="drone-map-panel__publish-toggle" onClick={() => { setFormAberto(true); setMensagem(''); }}>
          ＋ Publicar novo mapeamento
        </button>
      )}

      {podeEditar && formAberto && (
        <form className="drone-map-panel__form" onSubmit={publicar}>
          <label>
            Ortomosaico (GeoTIFF/COG, até 250 MB)
            <input type="file" accept=".tif,.tiff,image/tiff" onChange={selecionarArquivo} disabled={publicando} />
          </label>
          {arquivo && <small>{arquivo.name} · {formatDroneMappingSize(arquivo.size)}</small>}
          <label>
            Nome do levantamento
            <input
              type="text"
              value={nomeVoo}
              onChange={event => setNomeVoo(event.target.value)}
              placeholder="Ex.: Voo pós-plantio soja"
              maxLength={120}
              disabled={publicando}
            />
          </label>
          <label>
            Data do voo
            <input type="date" value={dataVoo} onChange={event => setDataVoo(event.target.value)} disabled={publicando} />
          </label>
          <footer>
            <button type="button" onClick={() => { setFormAberto(false); setArquivo(null); }} disabled={publicando}>Cancelar</button>
            <button type="submit" disabled={!arquivo || publicando}>{publicando ? 'Enviando…' : 'Publicar'}</button>
          </footer>
        </form>
      )}
    </section>
  );
}
